import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {parseApiErrorArrayInHttpErrorResponse} from '../common/api-error-array';
import {handleResponseError} from '../common/api-helpers';
import {AuthService} from '../components/auth/auth.service';
import {User} from '../models/user';
import {AppConfigService} from './config.service';

export interface UserSignUpRequest {
    login: string;
    fullName: string;
    email: string;
    password: string;
    referralId: string;
}

interface PasswordResetRequest {
    login: string; // User's login
}

interface PasswordChangeRequest {
    verificationCode: string; // A reset password code from email.
    password: string;
    twoFACode: string;
}

export const enum ChangePasswordError {
    PASSWORD_IS_INVALID = 'PASSWORD_IS_INVALID'
}

@Injectable()
export class SignUpService {
    private signUpUrl: string = this.appConfigService.api['signUp'];

    constructor(
        private httpClient: HttpClient,
        private appConfigService: AppConfigService,
        private authService: AuthService
    ) {}

    public createUser(userSignUpRequest: UserSignUpRequest): Observable<any> {
        const {email, login} = userSignUpRequest;

        userSignUpRequest = {
            ...userSignUpRequest,
            email: email && email.toLocaleLowerCase(),
            login: login && login.toLocaleLowerCase()
        };

        return this.httpClient.post(this.signUpUrl, userSignUpRequest);
    }

    public checkActivationCode(code: string): Observable<User> {
        const checkActivationCodeUrl: string = `${this.signUpUrl}/verify-email`;

        return this.httpClient.post<User>(
            checkActivationCodeUrl,
            {code}
        ).pipe(
            catchError(handleResponseError)
        );
    }

    public requestPasswordReset(login: string): Observable<any> {
        const passwordResetRequestUrl: string = `${this.signUpUrl}/reset-password-request`;
        const payload: PasswordResetRequest = {
            login: login.toLocaleLowerCase()
        };

        return this.httpClient.post(
            passwordResetRequestUrl,
            payload
        ).pipe(
            catchError(handleResponseError)
        );
    }

    public requestPasswordChange(
        resetPasswordRequestCode: string,
        password: string,
        twoFACode?: string
    ): Observable<any> {
        const passwordChangeRequestUrl: string = `${this.signUpUrl}/reset-password`;
        const payload: PasswordChangeRequest = {
            verificationCode: resetPasswordRequestCode,
            password: this.authService.encodePassword(password),
            twoFACode
        };

        return this.httpClient.put(passwordChangeRequestUrl, payload).pipe(
            parseApiErrorArrayInHttpErrorResponse()
        );
    }

}
